import InputLabel from 'components/atoms/InputLabel'
import LinkButton from 'components/atoms/LinkButton'
import { ProfileEditHeader } from 'components/organisms/Profile/ProfileEditHeader'
import { ProfileEditAvatarModal } from 'components/organisms/Profile/ProfileEditAvatarModal'
import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../core/i18n'
import { IJsonResponse } from '../../../core/JsonApiSerializer'
import { userService, utilService } from '../../../core/services'
import * as constants from '../../../static/constants'
import { Button, InputText, InputTextArea, ListItem, Panel, Select, StatusBadge } from '../../atoms'
import { CheckBoxes, Form } from '../../molecules'
import { FlashMessage } from '../../organisms'
import SettingsLayout from '../_layouts/settingsLayout'
import { Tabs } from 'components/molecules/Tabs'
import WithLabel from 'components/atoms/WithLabel'
import Typography from 'components/atoms/_layouts/Typography'

interface IWindow {
  flashMessages: FlashMessage
}
declare var window: IWindow

const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${constants.COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
  }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: constants.COLORS.primary,
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: constants.COLORS.white,
  }),
}

interface IProps {
  user: IJsonResponse
  display_languages: any[]
  active_path: string
  areas: any
  countries: any
}

const GUEST_FIELDS = {
  username: 'username',
  bio: 'bio',
  fullname: 'fullname',
  fullname_furi: 'fullname_furi',
  tel: 'tel',
  post_code: 'post_code',
  address: 'address',
  sub_email: 'sub_email',
  country_code: 'country_code',
}

const GUIDE_FIELDS = {
  username: 'username',
  fullname: 'fullname',
  fullname_furi: 'fullname_furi',
  tel: 'tel',
  post_code: 'post_code',
  address: 'address',
  sub_email: 'sub_email',
  country_code: 'country_code',
  display_language_ids: 'display_language_ids',
}

const AGENT_FIELDS = {
  username: 'username',
  fullname: 'fullname',
  fullname_furi: 'fullname_furi',
  tel: 'tel',
  post_code: 'post_code',
  address: 'address',
  sub_email: 'sub_email',
  country_code: 'country_code',
  display_language_ids: 'display_language_ids',
}

const DETAIL_FIELDS = {
  bio: 'bio',
  catchphrase: 'catchphrase',
}

const isEmptyValue = values => {
  return Object.keys(DETAIL_FIELDS).every(field => !values[field])
}

const ProfileEdit: React.FC<IProps> = props => {
  const { user: initialUser } = userService.getUserFromJson(props.user)
  const { data: displayLanguages } = utilService.getDataFromJson(props.display_languages)
  const [user, setUser] = React.useState(initialUser)
  const [showModal, setShowModal] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [previewImage, setPreviewImage] = React.useState(null)
  const [previewCoverImage, setPreviewCoverImage] = React.useState(null)
  const [selectedAreas, setSelectedAreas] = React.useState(
    initialUser.areas.length
      ? initialUser.areas.map(area => ({ value: String(area.id), label: area.name }))
      : null
  )

  const [localValue, setLocalValue] = React.useState(null)
  const [currentDetailId, setCurrentDetailId] = React.useState(null)

  const handleSubmit = async (initialValues, values) => {
    if ((user.guide || user.agent) && !values.display_language_ids?.length) {
      window.flashMessages.addMessage({
        text: I18n.locale === 'ja' ? '表示言語は必須項目です' : 'Display language is required',
        type: 'error',
      })

      return
    }
    const formData = new FormData()
    Object.keys(values).forEach(key => {
      if (key === 'display_language_ids') {
        values[key].map(v => {
          formData.append(`user[${key}][]`, v)
        })
      } else {
        formData.append(`user[${key}]`, values[key])
      }
    })

    if (previewImage) {
      formData.append('user[avatar]', previewImage.file)
    }
    if (previewCoverImage) {
      formData.append('user[cover]', previewCoverImage.file)
    }

    if (selectedAreas) {
      selectedAreas.forEach(area => {
        formData.append('user[area_ids][]', area.value)
      })
    } else {
      formData.append('user[area_ids][]', '')
    }
    setSaving(true)
    const { updatedUser, flush } = await userService.updateProfile(formData).finally(() => {
      setSaving(false)
    })

    setUser(updatedUser)
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
  }

  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false)
  const [isSubmitDetailEnabled, setIsSubmitDetailEnabled] = React.useState(false)

  const [errors, setErrors] = React.useState<any>({})

  const handleUpdateForm = (updatedErrors, updatedIsSubmitEnabled) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
  }

  const coverImage = React.useMemo(
    () => (previewCoverImage ? previewCoverImage.url : user.cover_url),
    [previewCoverImage, user.cover_url]
  )

  const formFields = React.useMemo(() => {
    if (user.guide) {
      return GUIDE_FIELDS
    }
    if (user.agent) {
      return AGENT_FIELDS
    }
    return GUEST_FIELDS
  }, [user.guide, user.agent])

  const updateUserDetail = React.useCallback(
    async params => {
      const { userDetail, flush } = await userService.updateUserDetail({
        ...params,
        id: currentDetailId,
      })

      setUser({
        ...user,
        user_details: user.user_details.map(detail => {
          if (detail.id === userDetail.id) {
            return userDetail
          }
          return detail
        }),
      })
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      return flush.type
    },
    [currentDetailId, user]
  )

  const onChangeTab = async (id: string | number) => {
    if (localValue?.bio && localValue?.catchphrase) {
      setSaving(true)
      const type = await updateUserDetail(localValue).finally(() => {
        setSaving(false)
      })
      if (type === 'success') {
        setCurrentDetailId(id)
      }
    } else if (isEmptyValue(localValue)) {
      setCurrentDetailId(id)
    }
  }

  const options = React.useMemo(
    () =>
      (user.user_details ?? []).map(({ id, display_language }) => {
        return {
          id,
          label: display_language.name_i18n,
        }
      }),
    [user]
  )
  const handleDetailSubmit = async (_, values) => {
    const params = {
      ...values,
      id: currentDetailId,
    }

    setSaving(true)
    const { flush } = await userService.updateUserDetail(params).finally(() => {
      setSaving(false)
    })

    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
  }
  const handleUpdateDetailForm = (updatedErrors, updatedIsSubmitEnabled, values) => {
    setErrors(updatedErrors)
    setIsSubmitDetailEnabled(updatedIsSubmitEnabled)
    if (values) {
      setLocalValue(values)
    }
  }
  const displayLanguagesChecked = React.useMemo(() => {
    if (user?.user_details && user?.user_details.length > 0) {
      return user.user_details.map(({ display_language_id }) => ({
        id: display_language_id,
      }))
    }
    return displayLanguages.map(({ id }) => ({ id }))
  }, [])
  React.useEffect(() => {
    if (currentDetailId === null && options.length > 0) {
      setCurrentDetailId(options[0].id)
    }
  }, [currentDetailId, options])

  return (
    <SettingsLayout
      active_path={props.active_path}
      user={props.user}
      backgroundColor={user.guest ? 'white' : 'default'}
    >
      <Main>
        <Panel
          headerImage={user.guest || user.admin ? null : coverImage}
          headerOpacity={true}
          noDecoration={true}
        >
          <ProfileEditHeader
            user={user}
            previewImage={previewImage}
            setPreviewCoverImage={setPreviewCoverImage}
            setShowModal={setShowModal}
          />
          <div className="Profile_Form">
            <Form
              fields={formFields}
              handleSubmit={handleSubmit}
              handleUpdateForm={handleUpdateForm}
            >
              <ListItem>
                <InputText
                  required={true}
                  name="fullname"
                  label={I18n.t('generic.fullname')}
                  defaultValue={user.fullname}
                  error={errors.fullname}
                />
              </ListItem>
              <ListItem>
                <InputText
                  required={false}
                  name="fullname_furi"
                  label={I18n.t('generic.fullname_furi')}
                  defaultValue={user.fullname_furi}
                  error={errors.fullname_furi}
                />
              </ListItem>
              <ListItem>
                <InputText
                  required={true}
                  name="username"
                  label={I18n.t('generic.nickname')}
                  defaultValue={user.username}
                  error={errors.username}
                />
              </ListItem>
              {user.guest && (
                <ListItem>
                  <InputTextArea
                    required={false}
                    name="bio"
                    label={I18n.t('generic.bio')}
                    defaultValue={user.bio}
                    error={errors.bio}
                  />
                </ListItem>
              )}
              {user.guide && (
                <ListItem>
                  <S.ReactSelectWrapper>
                    <p className="ReactSelect_Title">{I18n.t('generic.area')}</p>
                    <div className="ReactSelect_Main">
                      <ReactSelect
                        placeholder={I18n.t('generic.select_area')}
                        options={props.areas.map(area => ({
                          value: String(area.id),
                          label: area.name,
                        }))}
                        isMulti={true}
                        onChange={setSelectedAreas}
                        styles={selectCustomStyles}
                        defaultValue={selectedAreas}
                        menuPortalTarget={document.querySelector('body')}
                      />
                    </div>
                  </S.ReactSelectWrapper>
                </ListItem>
              )}
              <ListItem>
                <InputText
                  required={false}
                  name="tel"
                  label={I18n.t('generic.tel')}
                  defaultValue={user.tel}
                  error={errors.tel}
                />
              </ListItem>
              <ListItem>
                <InputText
                  required={false}
                  name="post_code"
                  label={I18n.t('generic.post_code')}
                  defaultValue={user.post_code}
                  error={errors.post_code}
                />
              </ListItem>
              <ListItem>
                <InputText
                  required={false}
                  name="address"
                  label={I18n.t('generic.address')}
                  defaultValue={user.address}
                  error={errors.address}
                />
              </ListItem>
              <ListItem>
                <Select
                  required={false}
                  name="country_code"
                  label={I18n.t('generic.country')}
                  defaultValue={user.country_code}
                  error={errors.country}
                  options={props.countries.map(country => ({
                    label: country.name_en,
                    value: country.code,
                  }))}
                />
              </ListItem>
              <ListItem>
                <InputText
                  required={false}
                  name="sub_email"
                  label={I18n.t('generic.sub_email')}
                  defaultValue={user.sub_email}
                  error={errors.sub_email}
                />
              </ListItem>
              {user.guide && user.agent && (
                <ListItem>
                  <div className="Authenticate">
                    <InputLabel label={I18n.t('generic.identification')} />
                    <div className="Authenticate_Content">
                      {user.identification_workflow_state === 'approved' ? (
                        <StatusBadge label={I18n.t('settings.authenticated')} type="success" />
                      ) : (
                        <>
                          <StatusBadge label={I18n.t('settings.unauthenticated')} type="caution" />
                          <LinkButton
                            href="/settings/identification"
                            miniLink={true}
                            showBorder={true}
                          >
                            {I18n.t('settings.set_identification')}
                          </LinkButton>
                        </>
                      )}
                    </div>
                  </div>
                </ListItem>
              )}
              {(user.guide || user.agent) && (
                <ListItem>
                  <CheckBoxes
                    options={displayLanguages}
                    checked={displayLanguagesChecked}
                    title={I18n.t('post.display_language')}
                    name="display_language_ids"
                    required={true}
                  />
                  <WithLabel label="">
                    <div>
                      <Typography color="caution">
                        {I18n.t('post.display_language_note')}
                      </Typography>
                    </div>
                  </WithLabel>
                </ListItem>
              )}
              <S.SubmitButtonWrapper>
                <Button primary={true} disabled={!isSubmitEnabled || saving}>
                  {I18n.t('generic.save')}
                </Button>
              </S.SubmitButtonWrapper>
            </Form>
            {(user.guide || user.agent) && user.user_details && currentDetailId && (
              <div style={{ marginTop: 16 }}>
                <Typography type="headline">{I18n.t('generic.detail')}</Typography>
                {user.user_details.map(
                  detail =>
                    detail.id === currentDetailId && (
                      <Form
                        key={`user-detail-${detail.id}`}
                        fields={DETAIL_FIELDS}
                        handleSubmit={handleDetailSubmit}
                        handleUpdateForm={handleUpdateDetailForm}
                      >
                        <div>
                          <Tabs
                            current={currentDetailId}
                            options={options}
                            handleSelect={onChangeTab}
                          />
                        </div>
                        <ListItem>
                          <InputText
                            required={true}
                            name="catchphrase"
                            label={I18n.t('generic.catchphrase')}
                            defaultValue={detail.catchphrase}
                            error={errors.catchphrase}
                          />
                        </ListItem>
                        <ListItem>
                          <InputTextArea
                            required={true}
                            name="bio"
                            label={I18n.t('generic.bio')}
                            defaultValue={detail.bio}
                            error={errors.bio}
                          />
                        </ListItem>
                        <S.SubmitButtonWrapper>
                          <Button primary={true} disabled={!isSubmitDetailEnabled || saving}>
                            {I18n.t('generic.save')}
                          </Button>
                        </S.SubmitButtonWrapper>
                      </Form>
                    )
                )}
              </div>
            )}
          </div>
        </Panel>
      </Main>
      {showModal && (
        <ProfileEditAvatarModal
          user={user}
          previewImage={previewImage}
          setPreviewImage={setPreviewImage}
          setShowModal={setShowModal}
        />
      )}
    </SettingsLayout>
  )
}

const Main = styled.div`
  .Profile_Form {
    margin-top: 178px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin-top: 100px;
    }
    .Authenticate {
      display: flex;
      align-items: center;
      justify-content: space-between;
      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        flex-direction: column;
      }
      .Authenticate_Content {
        width: 100%;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
      }
    }
  }
`

const S: { [key: string]: AnyStyledComponent } = {}
S.ListItemWrapper = styled.div`
  margin-top: 24px;
  .note {
    margin-left: 240px;
    font-size: 14px;
    margin-top: 5px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin-left: 0px;
    }
  }
`
S.ReactSelectWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 180px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 8px;

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
`
S.SubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
  gap: 20px;
  > .Button {
    width: 160px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: 120px;
    }
  }
`
export default ProfileEdit
